import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, MessageCircle, Trash2, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY = {
  name: "", tipo_pessoa: "J", cpf_cnpj: "", ie: "", company: "", email: "",
  phone: "", celular: "", whatsapp: "", endereco: "", bairro: "", cep: "",
  city: "", uf: "", site: "", tipo_contato: "Cliente", situacao: "A", segment: "", notes: "",
};
const TEXT_FIELDS = [
  ["cpf_cnpj", "CPF / CNPJ"], ["ie", "Inscrição Estadual"], ["company", "Nome fantasia"],
  ["email", "E-mail"], ["phone", "Fone"], ["celular", "Celular"], ["whatsapp", "WhatsApp"],
  ["endereco", "Endereço"], ["bairro", "Bairro"], ["cep", "CEP"], ["city", "Cidade"],
  ["uf", "UF"], ["site", "Site"], ["segment", "Segmento"],
];
const SIT = { A: "Ativo", I: "Inativo", S: "Sem movimento" };

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const nav = useNavigate();

  const load = () => api.get("/customers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const goWhatsapp = async (cid) => {
    try { const { data } = await api.post(`/customers/${cid}/whatsapp-conversation`); nav(`/app/conversas?conv=${data.conversation_id}`); }
    catch (e) { toast.error("Erro ao abrir conversa"); }
  };

  const create = async () => {
    if (!form.name) return toast.error("Nome obrigatório");
    await api.post("/customers", form); toast.success("Cliente criado");
    setOpen(false); setForm(EMPTY); load();
  };
  const del = async (id) => { await api.delete(`/customers/${id}`); load(); };
  const openDetail = async (id) => { const { data } = await api.get(`/customers/${id}`); setDetail(data); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Clientes</h1>
          <p className="text-slate-400 text-sm mt-1">Base comercial ATP — campos compatíveis com o Tiny ERP</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold shrink-0" data-testid="cust-new-btn"><Plus size={16} className="sm:mr-2" /><span className="hidden sm:inline">Novo cliente</span></Button>
      </div>

      <div className="hidden md:block rounded-xl border border-[#1E293B] bg-[#111622] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F17] text-slate-400">
            <tr>{["Nome", "Fantasia", "CPF/CNPJ", "Cidade/UF", "Tipo", "Ações"].map(h => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-12 text-slate-500">Sem clientes ainda.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-800/70 hover:bg-white/[0.02] cursor-pointer" onClick={() => openDetail(c.id)} data-testid={`cust-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-slate-300">{c.company || "—"}</td>
                <td className="px-4 py-3 text-slate-300">{c.cpf_cnpj || "—"}</td>
                <td className="px-4 py-3 text-slate-300">{[c.city, c.uf].filter(Boolean).join("/") || "—"}</td>
                <td className="px-4 py-3">{c.tiny_tipo || c.tipo_contato ? <Badge variant="outline" className="border-[#0CDBF2]/40 text-[#0CDBF2]">{c.tiny_tipo || c.tipo_contato}</Badge> : "—"}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-2">
                    {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" data-testid={`cust-wa-${c.id}`}>
                      <Button size="sm" variant="outline" className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10"><MessageCircle size={14} /></Button>
                    </a>}
                    <Button size="sm" variant="outline" onClick={() => del(c.id)} data-testid={`cust-del-${c.id}`}
                      className="border-slate-700 hover:bg-slate-800"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {items.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">Sem clientes ainda.</div>}
        {items.map((c) => (
          <div key={c.id} onClick={() => openDetail(c.id)} data-testid={`cust-card-${c.id}`}
            className="rounded-xl border border-[#1E293B] bg-[#111622] p-4 active:border-[#0CDBF2]/50">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{c.name}</div>
                {c.company && <div className="text-xs text-slate-400 truncate">{c.company}</div>}
                <div className="text-xs text-slate-500 mt-1">{[c.city, c.uf].filter(Boolean).join("/") || "—"}{c.cpf_cnpj ? ` · ${c.cpf_cnpj}` : ""}</div>
              </div>
              {(c.tiny_tipo || c.tipo_contato) && <Badge variant="outline" className="border-[#0CDBF2]/40 text-[#0CDBF2] shrink-0">{c.tiny_tipo || c.tipo_contato}</Badge>}
            </div>
            <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
              {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex-1" data-testid={`cust-wa-m-${c.id}`}>
                <Button size="sm" variant="outline" className="w-full border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10"><MessageCircle size={14} className="mr-1" />WhatsApp</Button></a>}
              <Button size="sm" variant="outline" onClick={() => del(c.id)} data-testid={`cust-del-m-${c.id}`} className="border-slate-700 hover:bg-slate-800"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo cliente</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-slate-300 text-sm">Nome*</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="cust-name" /></div>
            <div><Label className="text-slate-300 text-sm">Tipo de pessoa</Label>
              <select value={form.tipo_pessoa} onChange={(e) => setForm({ ...form, tipo_pessoa: e.target.value })} data-testid="cust-tipo-pessoa"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                <option value="J">Jurídica</option><option value="F">Física</option><option value="E">Estrangeiro</option>
              </select></div>
            <div><Label className="text-slate-300 text-sm">Tipo de contato</Label>
              <select value={form.tipo_contato} onChange={(e) => setForm({ ...form, tipo_contato: e.target.value })} data-testid="cust-tipo-contato"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                <option>Cliente</option><option>Fornecedor</option><option>Transportador</option><option>Outros</option>
              </select></div>
            {TEXT_FIELDS.map(([k, l]) => (
              <div key={k}><Label className="text-slate-300 text-sm">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid={`cust-${k}`} /></div>
            ))}
            <div><Label className="text-slate-300 text-sm">Situação</Label>
              <select value={form.situacao} onChange={(e) => setForm({ ...form, situacao: e.target.value })} data-testid="cust-situacao"
                className="w-full mt-1.5 bg-[#07090E] border border-slate-800 h-10 px-3 text-sm rounded-md">
                <option value="A">Ativo</option><option value="I">Inativo</option><option value="S">Sem movimento</option>
              </select></div>
            <div className="sm:col-span-2"><Label className="text-slate-300 text-sm">Observações</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#07090E] border-slate-800 mt-1.5" data-testid="cust-notes" /></div>
          </div>
          <Button onClick={create} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="cust-save">Salvar</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-[#0B0F17] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.customer?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => goWhatsapp(detail.customer.id)} className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25" data-testid="cust-btn-whatsapp"><MessageCircle size={14} className="mr-1" />Chamar no WhatsApp</Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/app/orcamentos?customer=${detail.customer.id}`)} className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10" data-testid="cust-btn-quote"><FileText size={14} className="mr-1" />Novo orçamento</Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/app/funil?customer=${detail.customer.id}`)} className="border-[#0CDBF2]/40 text-[#0CDBF2] hover:bg-[#0CDBF2]/10" data-testid="cust-btn-opp"><Plus size={14} className="mr-1" />Nova oportunidade</Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Fantasia:</span> {detail.customer.company || "—"}</div>
                <div><span className="text-slate-500">Tipo:</span> {detail.customer.tiny_tipo || detail.customer.tipo_contato || "—"} ({detail.customer.tipo_pessoa || "—"})</div>
                <div><span className="text-slate-500">CPF/CNPJ:</span> {detail.customer.cpf_cnpj || "—"}</div>
                <div><span className="text-slate-500">IE:</span> {detail.customer.ie || "—"}</div>
                <div><span className="text-slate-500">E-mail:</span> {detail.customer.email || "—"}</div>
                <div><span className="text-slate-500">Fone:</span> {detail.customer.phone || "—"}</div>
                <div><span className="text-slate-500">Celular:</span> {detail.customer.celular || "—"}</div>
                <div><span className="text-slate-500">WhatsApp:</span> {detail.customer.whatsapp || "—"}</div>
                <div className="col-span-2"><span className="text-slate-500">Endereço:</span> {[detail.customer.endereco, detail.customer.bairro, detail.customer.city, detail.customer.uf, detail.customer.cep].filter(Boolean).join(", ") || "—"}</div>
                <div><span className="text-slate-500">Site:</span> {detail.customer.site || "—"}</div>
                <div><span className="text-slate-500">Situação:</span> {SIT[detail.customer.situacao] || detail.customer.situacao || "—"}</div>
                <div><span className="text-slate-500">Segmento:</span> {detail.customer.segment || "—"}</div>
              </div>
              {detail.customer.notes && <div className="text-slate-300"><span className="text-slate-500">Obs:</span> {detail.customer.notes}</div>}
              <div>
                <div className="font-heading font-semibold mb-2">Oportunidades ({detail.opportunities.length})</div>
                {detail.opportunities.map((o) => <div key={o.id} className="border-b border-slate-800 py-2 text-sm">{o.title} — <span className="text-[#0CDBF2]">{o.stage_key}</span></div>)}
                {!detail.opportunities.length && <div className="text-slate-500 text-xs">Nenhuma</div>}
              </div>
              <div>
                <div className="font-heading font-semibold mb-2">Orçamentos ({detail.quotes.length})</div>
                {detail.quotes.map((q) => <div key={q.id} className="border-b border-slate-800 py-2 text-sm">{q.number} — {q.status} — R$ {q.total?.toFixed(2)}</div>)}
                {!detail.quotes.length && <div className="text-slate-500 text-xs">Nenhum</div>}
              </div>
              <div>
                <div className="font-heading font-semibold mb-2">Histórico de pedidos Tiny ({detail.tiny_orders?.length || 0})</div>
                {detail.tiny_orders?.map((o) => (
                  <div key={o.tiny_id} className="border-b border-slate-800 py-2 text-sm flex items-center justify-between" data-testid={`cust-tinyorder-${o.tiny_id}`}>
                    <span>Nº {o.numero || o.tiny_id} · <span className="text-slate-400">{o.situacao || "—"}</span>{o.data ? ` · ${o.data}` : ""}</span>
                    <span className="text-[#0CDBF2] font-mono">R$ {Number(o.valor || 0).toFixed(2)}</span>
                  </div>
                ))}
                {!detail.tiny_orders?.length && <div className="text-slate-500 text-xs">Nenhum</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
